import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { createRoot } from "react-dom/client";
import {
  BENCHMARK_BAR_STATS,
  BENCHMARK_INSTITUTION_COLORS,
  BENCHMARK_PIE_STATS,
  BENCHMARK_SUB_COLS,
  aggregateBenchmarkByInstitution,
  filterMetricsByMode,
  formatBenchmarkChartValue,
  formatBenchmarkMetricShortLabel,
} from "../../benchmarkMetricConfig";
import {
  BENCHMARK_MODES,
  ENTITY_ID_KEY_BY_MODE,
  ENTITY_LABEL_BY_MODE,
  collectMetricByYearSeries,
  normalizeRawDataForCharts,
} from "../../benchmarkEntityAdapters";
import {
  buildInstitutionMeanMinMax,
  buildInstitutionRankSeries,
  buildKeywordInstitutionMatrix,
} from "../../benchmarkChartDerivations";

/** 1-based column index → Excel column letters (1 → A, 27 → AA). */
const excelCol = (n) => {
  let s = "";
  let num = n;
  while (num > 0) {
    const mod = (num - 1) % 26;
    s = String.fromCharCode(65 + mod) + s;
    num = Math.floor((num - 1) / 26);
  }
  return s;
};

const thinBorder = {
  style: "thin",
  color: { argb: "FFCCCCCC" },
};

const PDF_MARGIN_MM = 12;

const fitImageToPage = (pdf, canvas, pageWidth, pageHeight) => {
  const imgData = canvas.toDataURL("image/png");
  const maxW = pageWidth - PDF_MARGIN_MM * 2;
  const maxH = pageHeight - PDF_MARGIN_MM * 2;
  let imgWidth = maxW;
  let imgHeight = (canvas.height * imgWidth) / canvas.width;
  if (imgHeight > maxH) {
    imgHeight = maxH;
    imgWidth = (canvas.width * imgHeight) / canvas.height;
  }
  const xOffset = (pageWidth - imgWidth) / 2;
  pdf.addImage(imgData, "PNG", xOffset, PDF_MARGIN_MM, imgWidth, imgHeight);
};

/**
 * Benchmark PDF: page 1 = legend + averaged metrics table; page 2 = pie charts (percent labels).
 */
export const exportBenchmarkPdf = async ({
  summaryElementId = "benchmark-pdf-summary",
  pieElementId = "benchmark-pdf-pie-capture",
  filename = "benchmark_charts.pdf",
  /** Extra ms after mount so off-screen ECharts finish layout before capture */
  pieReadyMs = 450,
} = {}) => {
  const pdf = new jsPDF("landscape", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const capture = async (elementId) => {
    const el = document.getElementById(elementId);
    if (!el) return null;
    return html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });
  };

  const summaryCanvas = await capture(summaryElementId);
  if (summaryCanvas) {
    fitImageToPage(pdf, summaryCanvas, pageWidth, pageHeight);
  }

  if (pieReadyMs > 0) {
    await new Promise((r) => setTimeout(r, pieReadyMs));
  }
  window.dispatchEvent(new Event("resize"));

  const pieCanvas = await capture(pieElementId);
  if (pieCanvas) {
    pdf.addPage();
    fitImageToPage(pdf, pieCanvas, pageWidth, pageHeight);
  }

  pdf.save(filename);
};

const applyBorders = (sheet, rowStart, rowEnd, colStart, colEnd) => {
  for (let r = rowStart; r <= rowEnd; r++) {
    for (let c = colStart; c <= colEnd; c++) {
      const cell = sheet.getCell(r, c);
      cell.border = {
        top: thinBorder,
        left: thinBorder,
        bottom: thinBorder,
        right: thinBorder,
      };
    }
  }
};

const styleHeaderRow = (sheet, row, fromCol, toCol) => {
  for (let c = fromCol; c <= toCol; c++) {
    const cell = sheet.getCell(row, c);
    cell.font = { bold: true };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  }
};

const addHeatmapSheet = (workbook, rawData, institutionIds, metricKey, metricLabel) => {
  const matrix = buildKeywordInstitutionMatrix(rawData, institutionIds, metricKey);
  const sheet = workbook.addWorksheet("Heatmap Matrix");
  const header = ["Research Topic", ...matrix.institutions.map((i) => i.name)];
  sheet.addRow([`Metric: ${metricLabel}`]);
  sheet.addRow(header);

  matrix.matrixRows.forEach((row) => {
    sheet.addRow([
      row.keyword,
      ...row.values.map((v) => (v.value == null ? "" : v.value)),
    ]);
  });

  sheet.mergeCells(1, 1, 1, header.length);
  sheet.getCell(1, 1).font = { bold: true };
  styleHeaderRow(sheet, 2, 1, header.length);
  applyBorders(sheet, 2, matrix.matrixRows.length + 2, 1, header.length);
  sheet.getColumn(1).width = 28;
  for (let c = 2; c <= header.length; c++) sheet.getColumn(c).width = 18;
};

const addRankSheet = (workbook, rawData, institutionIds, metricKey, metricLabel) => {
  const rank = buildInstitutionRankSeries(rawData, institutionIds, metricKey);
  const sheet = workbook.addWorksheet("Rank by Keyword");
  const header = ["Institution", ...rank.keywords];
  sheet.addRow([`Metric: ${metricLabel}`]);
  sheet.addRow(header);
  rank.lines.forEach((line) => {
    sheet.addRow([
      line.name,
      ...line.ranks.map((r) => (r == null ? "" : r)),
    ]);
  });
  sheet.mergeCells(1, 1, 1, header.length);
  sheet.getCell(1, 1).font = { bold: true };
  styleHeaderRow(sheet, 2, 1, header.length);
  applyBorders(sheet, 2, rank.lines.length + 2, 1, header.length);
  sheet.getColumn(1).width = 30;
  for (let c = 2; c <= header.length; c++) sheet.getColumn(c).width = 16;
};

const addVariabilitySheet = (workbook, rawData, institutionIds, metricKey, metricLabel) => {
  const variability = buildInstitutionMeanMinMax(rawData, institutionIds, metricKey);
  const sheet = workbook.addWorksheet("Mean Min Max");
  const header = ["Institution", "Count", "Min", "Mean", "Max"];
  sheet.addRow([`Metric: ${metricLabel}`]);
  sheet.addRow(header);
  variability.rows.forEach((r) => {
    sheet.addRow([
      r.name,
      r.count,
      r.min == null ? "" : r.min,
      r.mean == null ? "" : r.mean,
      r.max == null ? "" : r.max,
    ]);
  });
  sheet.mergeCells(1, 1, 1, header.length);
  sheet.getCell(1, 1).font = { bold: true };
  styleHeaderRow(sheet, 2, 1, header.length);
  applyBorders(sheet, 2, variability.rows.length + 2, 1, header.length);
  [28, 10, 14, 14, 14].forEach((w, idx) => {
    sheet.getColumn(idx + 1).width = w;
  });
};

/**
 * Export breakdown table data to an Excel file (merged headers, centered bold rows 1–2, full borders).
 */
export const exportTableToExcel = async (
  rawData,
  institutionIds,
  filename,
  options = {}
) => {
  if (!rawData || rawData.length === 0) return;
  const metricKey = options.metricKey || BENCHMARK_BAR_STATS[0]?.key;
  const metricLabel =
    BENCHMARK_BAR_STATS.find((m) => m.key === metricKey)?.label || metricKey;

  const firstEntry = rawData[0];
  const firstKeyword = Object.keys(firstEntry)[0];
  const instNameMap = {};
  (firstEntry[firstKeyword] || []).forEach((inst) => {
    instNameMap[inst.institution_id] = inst.name;
  });

  const orderedIds = institutionIds.filter((id) => instNameMap[id]);
  const subCount = BENCHMARK_SUB_COLS.length;
  const totalCols = 1 + orderedIds.length * subCount;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Keyword Breakdown");

  // Merge A1:A2 — "Research Topic" centered vertically and horizontally
  sheet.mergeCells("A1:A2");
  const corner = sheet.getCell(1, 1);
  corner.value = "Research Topic";
  corner.font = { bold: true };
  corner.alignment = { horizontal: "center", vertical: "middle", wrapText: true };

  // Row 1: institution names (each merged across its metric columns)
  let col = 2;
  for (const id of orderedIds) {
    const startCol = col;
    const endCol = col + subCount - 1;
    const startL = excelCol(startCol);
    const endL = excelCol(endCol);
    if (startCol < endCol) {
      sheet.mergeCells(`${startL}1:${endL}1`);
    }
    const instCell = sheet.getCell(1, startCol);
    instCell.value = instNameMap[id] || id;
    instCell.font = { bold: true };
    instCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    col = endCol + 1;
  }

  // Row 2: metric sub-headers (columns B onward)
  col = 2;
  for (let i = 0; i < orderedIds.length; i++) {
    for (const sub of BENCHMARK_SUB_COLS) {
      const c = sheet.getCell(2, col);
      c.value = sub.label;
      c.font = { bold: true };
      c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      col++;
    }
  }

  // Data from row 3
  let rowIdx = 3;
  for (const entry of rawData) {
    const keyword = Object.keys(entry)[0];
    const instLookup = {};
    (entry[keyword] || []).forEach((inst) => {
      instLookup[inst.institution_id] = inst;
    });

    sheet.getCell(rowIdx, 1).value = keyword;
    col = 2;
    for (const id of orderedIds) {
      const data = instLookup[id] || {};
      for (const sub of BENCHMARK_SUB_COLS) {
        const v = data[sub.key];
        sheet.getCell(rowIdx, col).value = v != null ? v : "";
        col++;
      }
    }
    rowIdx++;
  }

  const lastRow = rowIdx - 1;

  // Borders on the full used range
  applyBorders(sheet, 1, lastRow, 1, totalCols);

  sheet.getColumn(1).width = 28;
  let wcol = 2;
  for (let i = 0; i < orderedIds.length; i++) {
    for (const sub of BENCHMARK_SUB_COLS) {
      sheet.getColumn(wcol).width = Math.min(22, Math.max(10, sub.label.length + 4));
      wcol++;
    }
  }

  addHeatmapSheet(workbook, rawData, orderedIds, metricKey, metricLabel);
  addRankSheet(workbook, rawData, orderedIds, metricKey, metricLabel);
  addVariabilitySheet(workbook, rawData, orderedIds, metricKey, metricLabel);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename || `benchmark_breakdown_${fileTimestamp()}.xlsx`);
};

// ──────────────────────────────────────────────────────────────────────────
// Research Topics Overview exports
//
// PDF and Excel reports for the Research Topics Overview mode of
// `ResultsView`. Both consume `dashboardData` from `getKeywordDashboardContext`
// plus the `selectedKeywords` chips. The section list mirrors the on-screen
// `ENTITY_SECTIONS` declared in
// `Components/ResearchTopicsOverviewCharts/ResearchTopicsOverviewCharts.jsx`
// (kept private here so the export utils never touch the UI files; if a new
// metric or list key is added there, mirror it in OVERVIEW_EXPORT_SECTIONS).
// ──────────────────────────────────────────────────────────────────────────

const OVERVIEW_EXPORT_SECTIONS = [
  {
    key: "institutions",
    sheetName: "Top Institutions",
    entityLabel: "institutions",
    metrics: [
      {
        key: "publication_count",
        label: "Publications",
        listKey: "top_institutions_by_publication_count",
      },
      {
        key: "citation_count",
        label: "Citations",
        listKey: "top_institutions_by_citation_count",
      },
      {
        key: "grants_count",
        label: "Grants",
        listKey: "top_institutions_by_grants_count",
      },
    ],
  },
  {
    key: "researchers",
    sheetName: "Top Researchers",
    entityLabel: "researchers",
    metrics: [
      {
        key: "publication_count",
        label: "Publications",
        listKey: "top_faculty_by_publication_count",
      },
      {
        key: "citation_count",
        label: "Citations",
        listKey: "top_faculty_by_citation_count",
      },
    ],
  },
  {
    key: "units",
    sheetName: "Top Units",
    entityLabel: "units",
    metrics: [
      {
        key: "publication_count",
        label: "Publications",
        listKey: "top_unit_by_publication_count",
      },
      {
        key: "citation_count",
        label: "Citations",
        listKey: "top_unit_by_citation_count",
      },
    ],
  },
  {
    key: "venues",
    sheetName: "Top Venues",
    entityLabel: "venues",
    metrics: [
      {
        key: "publication_count",
        label: "Publications",
        listKey: "top_venue_by_publication_count",
      },
      {
        key: "citation_count",
        label: "Citations",
        listKey: "top_venue_by_citation_count",
      },
    ],
  },
];

const OVERVIEW_KPI_FIELDS = [
  { key: "publication_count", label: "Publications" },
  { key: "citation_count", label: "Citations" },
  { key: "faculty_count", label: "Faculty" },
  { key: "grants_count", label: "Grants" },
];

const overviewCapitalize = (s) =>
  String(s ?? "").replace(/\b\w/g, (c) => c.toUpperCase());

const overviewFormatKpi = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
};

const overviewGetRows = (dashboardData, listKey, metricKey) => {
  const list = Array.isArray(dashboardData?.[listKey])
    ? dashboardData[listKey]
    : [];
  return list
    .map((item, idx) => ({
      rank: idx + 1,
      name: item?.name || "—",
      value: Number(item?.[metricKey]) || 0,
    }))
    .filter((row) => row.value > 0);
};

// ─── PDF ──────────────────────────────────────────────────────────────────

const PDF_OVERVIEW_MARGIN = 15;
const PDF_OVERVIEW_TABLE_ROW_HEIGHT = 7;
const PDF_OVERVIEW_TABLE_HEADER_HEIGHT = 8;

/** Binary-search ellipsis truncation so cell text always fits its column. */
const truncatePdfTextToWidth = (pdf, text, maxWidth) => {
  const original = String(text ?? "");
  if (!original) return "";
  if (pdf.getTextWidth(original) <= maxWidth) return original;
  const ellipsis = "…";
  let lo = 0;
  let hi = original.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if (pdf.getTextWidth(original.slice(0, mid) + ellipsis) <= maxWidth) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return original.slice(0, lo) + ellipsis;
};

const drawOverviewTableHeader = (pdf, x, y, columns) => {
  const totalW = columns.reduce((a, c) => a + c.width, 0);
  pdf.setFillColor(241, 245, 249);
  pdf.rect(x, y, totalW, PDF_OVERVIEW_TABLE_HEADER_HEIGHT, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(30, 41, 59);

  let cx = x;
  columns.forEach((c) => {
    const align = c.align || "left";
    const tx =
      align === "right"
        ? cx + c.width - 3
        : align === "center"
          ? cx + c.width / 2
          : cx + 3;
    pdf.text(c.label, tx, y + PDF_OVERVIEW_TABLE_HEADER_HEIGHT / 2 + 1.4, {
      align,
    });
    cx += c.width;
  });

  pdf.setDrawColor(203, 213, 225);
  pdf.setLineWidth(0.2);
  pdf.rect(x, y, totalW, PDF_OVERVIEW_TABLE_HEADER_HEIGHT);
};

const drawOverviewTableRow = (pdf, x, y, columns, values, isAlt) => {
  const totalW = columns.reduce((a, c) => a + c.width, 0);
  if (isAlt) {
    pdf.setFillColor(248, 250, 252);
    pdf.rect(x, y, totalW, PDF_OVERVIEW_TABLE_ROW_HEIGHT, "F");
  }

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(51, 65, 85);

  let cx = x;
  columns.forEach((c, i) => {
    const align = c.align || "left";
    const padding = 3;
    const innerWidth = c.width - padding * 2;
    const text = truncatePdfTextToWidth(pdf, values[i], innerWidth);
    const tx =
      align === "right"
        ? cx + c.width - padding
        : align === "center"
          ? cx + c.width / 2
          : cx + padding;
    pdf.text(text, tx, y + PDF_OVERVIEW_TABLE_ROW_HEIGHT / 2 + 1.4, { align });
    cx += c.width;
  });

  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.15);
  pdf.rect(x, y, totalW, PDF_OVERVIEW_TABLE_ROW_HEIGHT);
};

const drawOverviewPageHeader = (pdf, title, pageWidth) => {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.setTextColor(15, 23, 42);
  pdf.text(title, PDF_OVERVIEW_MARGIN, PDF_OVERVIEW_MARGIN + 2);

  pdf.setDrawColor(15, 52, 96);
  pdf.setLineWidth(0.6);
  pdf.line(
    PDF_OVERVIEW_MARGIN,
    PDF_OVERVIEW_MARGIN + 5,
    pageWidth - PDF_OVERVIEW_MARGIN,
    PDF_OVERVIEW_MARGIN + 5
  );
};

/**
 * Programmatic PDF (no html2canvas / DOM capture). Page 1 = research topic
 * list + 4 overall KPIs; subsequent pages = one Top-N table per (entity ×
 * metric) listKey present in `dashboardData`.
 */
export const exportOverviewPdf = ({
  selectedKeywords = [],
  dashboardData = null,
  filename,
} = {}) => {
  const pdf = new jsPDF("portrait", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PDF_OVERVIEW_MARGIN * 2;

  // ── Page 1: Summary ────────────────────────────────────────────────────
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.setTextColor(15, 23, 42);
  pdf.text(
    "Research Topics Benchmark Report",
    PDF_OVERVIEW_MARGIN,
    PDF_OVERVIEW_MARGIN + 4
  );

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(100, 116, 139);
  pdf.text(
    `Generated ${new Date().toLocaleString()}`,
    PDF_OVERVIEW_MARGIN,
    PDF_OVERVIEW_MARGIN + 11
  );

  pdf.setDrawColor(15, 52, 96);
  pdf.setLineWidth(0.8);
  pdf.line(
    PDF_OVERVIEW_MARGIN,
    PDF_OVERVIEW_MARGIN + 14,
    pageWidth - PDF_OVERVIEW_MARGIN,
    PDF_OVERVIEW_MARGIN + 14
  );

  let y = PDF_OVERVIEW_MARGIN + 22;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(15, 23, 42);
  pdf.text("Selected Research Topics", PDF_OVERVIEW_MARGIN, y);
  y += 7;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(51, 65, 85);

  if (!selectedKeywords.length) {
    pdf.setTextColor(148, 163, 184);
    pdf.text("(none selected)", PDF_OVERVIEW_MARGIN + 4, y);
    y += 6;
    pdf.setTextColor(51, 65, 85);
  } else {
    selectedKeywords.forEach((kw) => {
      const label = `• ${overviewCapitalize(kw)}`;
      const lines = pdf.splitTextToSize(label, contentWidth - 4);
      lines.forEach((line) => {
        if (y > pageHeight - PDF_OVERVIEW_MARGIN) {
          pdf.addPage();
          y = PDF_OVERVIEW_MARGIN + 6;
        }
        pdf.text(line, PDF_OVERVIEW_MARGIN + 4, y);
        y += 6;
      });
    });
  }

  y += 6;
  if (y > pageHeight - 60) {
    pdf.addPage();
    y = PDF_OVERVIEW_MARGIN + 6;
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(15, 23, 42);
  pdf.text("Overall Statistics", PDF_OVERVIEW_MARGIN, y);
  y += 4;

  const kpiColumns = [
    { label: "Metric", width: contentWidth * 0.55, align: "left" },
    { label: "Value", width: contentWidth * 0.45, align: "right" },
  ];
  drawOverviewTableHeader(pdf, PDF_OVERVIEW_MARGIN, y + 4, kpiColumns);
  let rowY = y + 4 + PDF_OVERVIEW_TABLE_HEADER_HEIGHT;
  OVERVIEW_KPI_FIELDS.forEach((kpi, idx) => {
    drawOverviewTableRow(
      pdf,
      PDF_OVERVIEW_MARGIN,
      rowY,
      kpiColumns,
      [
        formatBenchmarkMetricShortLabel(kpi.label),
        overviewFormatKpi(dashboardData?.[kpi.key]),
      ],
      idx % 2 === 1
    );
    rowY += PDF_OVERVIEW_TABLE_ROW_HEIGHT;
  });

  // ── Per-stat pages ─────────────────────────────────────────────────────
  OVERVIEW_EXPORT_SECTIONS.forEach((section) => {
    section.metrics.forEach((metric) => {
      const rows = overviewGetRows(dashboardData, metric.listKey, metric.key);
      if (!rows.length) return;

      pdf.addPage();
      const metricLabel = formatBenchmarkMetricShortLabel(metric.label);
      const title = `Top ${overviewCapitalize(section.entityLabel)} by ${metricLabel}`;
      drawOverviewPageHeader(pdf, title, pageWidth);

      const columns = [
        { label: "Rank", width: 18, align: "center" },
        { label: "Name", width: contentWidth - 18 - 38, align: "left" },
        { label: metricLabel, width: 38, align: "right" },
      ];

      let cursorY = PDF_OVERVIEW_MARGIN + 12;
      drawOverviewTableHeader(pdf, PDF_OVERVIEW_MARGIN, cursorY, columns);
      cursorY += PDF_OVERVIEW_TABLE_HEADER_HEIGHT;

      rows.forEach((row, idx) => {
        if (cursorY + PDF_OVERVIEW_TABLE_ROW_HEIGHT > pageHeight - PDF_OVERVIEW_MARGIN) {
          pdf.addPage();
          drawOverviewPageHeader(pdf, `${title} (continued)`, pageWidth);
          cursorY = PDF_OVERVIEW_MARGIN + 12;
          drawOverviewTableHeader(pdf, PDF_OVERVIEW_MARGIN, cursorY, columns);
          cursorY += PDF_OVERVIEW_TABLE_HEADER_HEIGHT;
        }
        drawOverviewTableRow(
          pdf,
          PDF_OVERVIEW_MARGIN,
          cursorY,
          columns,
          [
            String(row.rank),
            row.name,
            formatBenchmarkChartValue(row.value, 0, { abbreviate: false }),
          ],
          idx % 2 === 1
        );
        cursorY += PDF_OVERVIEW_TABLE_ROW_HEIGHT;
      });
    });
  });

  pdf.save(filename || `research_topics_overview_${fileTimestamp()}.pdf`);
};

// ─── Excel ────────────────────────────────────────────────────────────────

const writeOverviewSummarySheet = (workbook, selectedKeywords, dashboardData) => {
  const sheet = workbook.addWorksheet("Summary");

  sheet.mergeCells("A1:B1");
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = "Research Topics Benchmark Report";
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: "left", vertical: "middle" };

  sheet.getCell(2, 1).value = "Generated";
  sheet.getCell(2, 1).font = { bold: true };
  sheet.getCell(2, 2).value = new Date().toLocaleString();

  let row = 4;
  sheet.mergeCells(row, 1, row, 2);
  const topicsHeader = sheet.getCell(row, 1);
  topicsHeader.value = "Selected Research Topics";
  topicsHeader.font = { bold: true };
  topicsHeader.alignment = { horizontal: "left", vertical: "middle" };
  row += 1;

  if (!selectedKeywords.length) {
    sheet.getCell(row, 1).value = "(none selected)";
    sheet.getCell(row, 1).font = { italic: true, color: { argb: "FF94A3B8" } };
    row += 1;
  } else {
    selectedKeywords.forEach((kw) => {
      sheet.getCell(row, 1).value = overviewCapitalize(kw);
      row += 1;
    });
  }

  row += 1;
  sheet.mergeCells(row, 1, row, 2);
  const kpiHeader = sheet.getCell(row, 1);
  kpiHeader.value = "Overall Statistics";
  kpiHeader.font = { bold: true };
  kpiHeader.alignment = { horizontal: "left", vertical: "middle" };
  row += 1;

  const kpiHeaderRow = row;
  sheet.getCell(row, 1).value = "Metric";
  sheet.getCell(row, 2).value = "Value";
  styleHeaderRow(sheet, row, 1, 2);
  row += 1;

  OVERVIEW_KPI_FIELDS.forEach((kpi) => {
    sheet.getCell(row, 1).value = kpi.label;
    const v = dashboardData?.[kpi.key];
    sheet.getCell(row, 2).value =
      v == null || Number.isNaN(Number(v)) ? "—" : Number(v);
    if (typeof sheet.getCell(row, 2).value === "number") {
      sheet.getCell(row, 2).numFmt = "#,##0";
    }
    sheet.getCell(row, 2).alignment = { horizontal: "right" };
    row += 1;
  });

  applyBorders(sheet, kpiHeaderRow, row - 1, 1, 2);

  sheet.getColumn(1).width = 36;
  sheet.getColumn(2).width = 24;

  return sheet;
};

const writeOverviewEntitySheet = (workbook, section, dashboardData) => {
  const blocks = section.metrics
    .map((metric) => ({
      metric,
      rows: overviewGetRows(dashboardData, metric.listKey, metric.key),
    }))
    .filter((b) => b.rows.length > 0);

  if (!blocks.length) return null;

  const sheet = workbook.addWorksheet(section.sheetName);
  let row = 1;

  blocks.forEach(({ metric, rows }, idx) => {
    if (idx > 0) row += 1;

    sheet.mergeCells(row, 1, row, 3);
    const titleCell = sheet.getCell(row, 1);
    titleCell.value = `Top ${overviewCapitalize(section.entityLabel)} by ${metric.label}`;
    titleCell.font = { bold: true, size: 12 };
    titleCell.alignment = { horizontal: "left", vertical: "middle" };
    row += 1;

    const headerRow = row;
    sheet.getCell(row, 1).value = "Rank";
    sheet.getCell(row, 2).value = "Name";
    sheet.getCell(row, 3).value = metric.label;
    styleHeaderRow(sheet, row, 1, 3);
    row += 1;

    const dataStart = row;
    rows.forEach((r) => {
      sheet.getCell(row, 1).value = r.rank;
      sheet.getCell(row, 1).alignment = { horizontal: "center" };
      sheet.getCell(row, 2).value = r.name;
      sheet.getCell(row, 3).value = r.value;
      sheet.getCell(row, 3).numFmt = "#,##0";
      sheet.getCell(row, 3).alignment = { horizontal: "right" };
      row += 1;
    });

    applyBorders(sheet, headerRow, dataStart + rows.length - 1, 1, 3);
  });

  sheet.getColumn(1).width = 8;
  sheet.getColumn(2).width = 40;
  sheet.getColumn(3).width = 18;

  return sheet;
};

/**
 * Excel workbook for Research Topics Overview: a `Summary` sheet plus one
 * sheet per entity (Top Institutions / Researchers / Units / Venues) with
 * stacked Top-N tables for each metric whose list has data.
 */
export const exportOverviewToExcel = async ({
  selectedKeywords = [],
  dashboardData = null,
  filename,
} = {}) => {
  const workbook = new ExcelJS.Workbook();
  writeOverviewSummarySheet(workbook, selectedKeywords, dashboardData);

  OVERVIEW_EXPORT_SECTIONS.forEach((section) => {
    writeOverviewEntitySheet(workbook, section, dashboardData);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename || `research_topics_overview_${fileTimestamp()}.xlsx`);
};

// ──────────────────────────────────────────────────────────────────────────
// Institution Benchmark exports (Benchmark by institution mode)
//
// PDF report: page 1 = selected research topics + institutions, page 2 = six
// small pies with legend, value-annotated slices, and a combined per-
// institution table, page 3+ = the Research Topics Specific Breakdown
// matrix (shrink-fit landscape A4). Excel workbook: single sheet with the
// breakdown only (mirrors the on-screen `entityRows` layout from
// `BreakdownTable.jsx`).
// ──────────────────────────────────────────────────────────────────────────

const INSTITUTION_PDF_MARGIN = 12;

const hexToRgb = (hex) => {
  const h = String(hex || "").replace("#", "").trim();
  if (h.length !== 6) return [0, 0, 0];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [
    Number.isFinite(r) ? r : 0,
    Number.isFinite(g) ? g : 0,
    Number.isFinite(b) ? b : 0,
  ];
};

/**
 * Draw a closed donut sector by approximating the arcs with many-segment
 * polylines (jsPDF has no native arc primitive). Angles are math-radians
 * referenced to PDF coordinates where +y is downward; pies are oriented so
 * `startAngle = -π/2` corresponds to 12 o'clock and increasing angles sweep
 * clockwise. Used so PDF pies match the on-screen `PieChart` (ECharts
 * radius `["42%", "72%"]`).
 */
const drawPdfDonutSector = (
  pdf,
  cx,
  cy,
  outerR,
  innerR,
  startAngle,
  endAngle,
  fillRGB,
  strokeRGB = [255, 255, 255]
) => {
  const sweep = endAngle - startAngle;
  if (sweep <= 1e-6) return;

  pdf.setFillColor(fillRGB[0], fillRGB[1], fillRGB[2]);
  pdf.setDrawColor(strokeRGB[0], strokeRGB[1], strokeRGB[2]);
  pdf.setLineWidth(0.4);

  if (sweep >= Math.PI * 2 - 1e-6) {
    pdf.circle(cx, cy, outerR, "F");
    pdf.setFillColor(255, 255, 255);
    pdf.circle(cx, cy, innerR, "F");
    return;
  }

  const segs = Math.max(8, Math.ceil((sweep / (Math.PI * 2)) * 96));
  const lines = [];
  const startX = cx + outerR * Math.cos(startAngle);
  const startY = cy + outerR * Math.sin(startAngle);
  let prevX = startX;
  let prevY = startY;

  for (let i = 1; i <= segs; i++) {
    const t = startAngle + (i / segs) * sweep;
    const xi = cx + outerR * Math.cos(t);
    const yi = cy + outerR * Math.sin(t);
    lines.push([xi - prevX, yi - prevY]);
    prevX = xi;
    prevY = yi;
  }

  const innerEndX = cx + innerR * Math.cos(endAngle);
  const innerEndY = cy + innerR * Math.sin(endAngle);
  lines.push([innerEndX - prevX, innerEndY - prevY]);
  prevX = innerEndX;
  prevY = innerEndY;

  for (let i = 1; i <= segs; i++) {
    const t = endAngle - (i / segs) * sweep;
    const xi = cx + innerR * Math.cos(t);
    const yi = cy + innerR * Math.sin(t);
    lines.push([xi - prevX, yi - prevY]);
    prevX = xi;
    prevY = yi;
  }

  pdf.lines(lines, startX, startY, [1, 1], "FD", true);
};

/**
 * Place a value annotation just outside a slice's outer edge, picking
 * left/center/right text alignment based on which side of the pie it falls
 * on. Skips slices whose share is below ~4% to prevent overlap.
 */
const drawPdfSliceAnnotation = (
  pdf,
  cx,
  cy,
  radius,
  midAngle,
  label,
  share
) => {
  if (share < 0.04) return;
  const labelR = radius + 4;
  const lx = cx + labelR * Math.cos(midAngle);
  const ly = cy + labelR * Math.sin(midAngle);
  const cosA = Math.cos(midAngle);
  const align = cosA > 0.2 ? "left" : cosA < -0.2 ? "right" : "center";

  // Short leader tick from the slice edge to the label.
  const tickStartX = cx + (radius + 0.5) * Math.cos(midAngle);
  const tickStartY = cy + (radius + 0.5) * Math.sin(midAngle);
  pdf.setDrawColor(160, 174, 192);
  pdf.setLineWidth(0.2);
  pdf.line(tickStartX, tickStartY, lx, ly);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(45, 55, 72);
  pdf.text(label, lx, ly + 1.2, { align });
};

/**
 * Render one pie tile in the on-screen `PieChart` style: soft gray rounded
 * card background, UPPERCASE metric label, muted "Total: ..." subtitle, a
 * donut chart, and outer value annotations (the only addition over the UI).
 */
const drawPdfPieTile = (pdf, bounds, stat, institutions, colorMap) => {
  const { x, y, width, height } = bounds;

  // Tile background to match the on-screen card.
  pdf.setFillColor(248, 250, 252);
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(x + 1, y + 1, width - 2, height - 2, 1.8, 1.8, "FD");

  const totalValue = institutions.reduce(
    (sum, inst) => sum + (Number(inst[stat.key]) || 0),
    0
  );

  // UPPERCASE small-caps title.
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(71, 85, 105);
  pdf.text(
    formatBenchmarkMetricShortLabel(stat.label).toUpperCase(),
    x + width / 2,
    y + 6,
    { align: "center" }
  );

  // Total subtitle — muted gray, smaller font.
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(148, 163, 184);
  pdf.text(
    `Total: ${formatBenchmarkChartValue(totalValue, stat.decimals ?? 0)}`,
    x + width / 2,
    y + 10,
    { align: "center" }
  );

  if (totalValue <= 0) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor(148, 163, 184);
    pdf.text("No data", x + width / 2, y + height / 2 + 2, { align: "center" });
    return;
  }

  // Donut placement leaves room for top labels and outer annotations.
  const cx = x + width / 2;
  const cy = y + 14 + (height - 16) / 2;
  const outerR = Math.max(
    6,
    Math.min(width / 2 - 14, (height - 18) / 2 - 4)
  );
  const innerR = outerR * 0.5;

  let cursorAngle = -Math.PI / 2;
  institutions.forEach((inst) => {
    const value = Number(inst[stat.key]) || 0;
    if (value <= 0) return;
    const sweep = (value / totalValue) * Math.PI * 2;
    const startAngle = cursorAngle;
    const endAngle = cursorAngle + sweep;
    const fillRGB = hexToRgb(
      colorMap[inst.institution_id] || BENCHMARK_INSTITUTION_COLORS[0]
    );
    drawPdfDonutSector(
      pdf,
      cx,
      cy,
      outerR,
      innerR,
      startAngle,
      endAngle,
      fillRGB
    );
    cursorAngle = endAngle;
  });

  // Second pass for annotations so text always renders above slices.
  cursorAngle = -Math.PI / 2;
  institutions.forEach((inst) => {
    const value = Number(inst[stat.key]) || 0;
    if (value <= 0) return;
    const sweep = (value / totalValue) * Math.PI * 2;
    const midAngle = cursorAngle + sweep / 2;
    const share = value / totalValue;
    drawPdfSliceAnnotation(
      pdf,
      cx,
      cy,
      outerR,
      midAngle,
      formatBenchmarkChartValue(value, stat.decimals ?? 0),
      share
    );
    cursorAngle += sweep;
  });
};

/**
 * Draw the entity legend strip across the top of the pie page (one entry
 * per institution / researcher / unit / venue). Wraps onto multiple rows
 * when entries don't fit on one line. Returns the y after the last row.
 */
const drawPdfInstitutionLegend = (
  pdf,
  startX,
  startY,
  contentWidth,
  entityIds,
  entityNames,
  colorMap
) => {
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);

  const swatch = 4;
  const gapAfterSwatch = 1.8;
  const gapBetween = 6;
  const rowHeight = 6;

  let cursorX = startX;
  let cursorY = startY;

  entityIds.forEach((id) => {
    const name = entityNames[id] || id;
    const textWidth = pdf.getTextWidth(name);
    const entryWidth = swatch + gapAfterSwatch + textWidth;
    if (cursorX + entryWidth > startX + contentWidth) {
      cursorX = startX;
      cursorY += rowHeight;
    }
    const fillRGB = hexToRgb(colorMap[id] || BENCHMARK_INSTITUTION_COLORS[0]);
    pdf.setFillColor(fillRGB[0], fillRGB[1], fillRGB[2]);
    pdf.setDrawColor(fillRGB[0], fillRGB[1], fillRGB[2]);
    pdf.roundedRect(cursorX, cursorY - swatch + 1, swatch, swatch, 0.6, 0.6, "F");
    pdf.setTextColor(30, 41, 59);
    pdf.text(name, cursorX + swatch + gapAfterSwatch, cursorY + 0.5);
    cursorX += entryWidth + gapBetween;
  });

  return cursorY + rowHeight;
};

// ─── Citations Over Time (researcher / unit / venue) ─────────────────────

// Mirror of `CITATION_LINE_COLORS` in `CitationByYearChart.jsx`. Used as a
// fallback when the page-2 entity colorMap doesn't have an entry.
const CITATION_LINE_COLORS_PDF = [
  "#1f4e8c",
  "#b23a48",
  "#2f6fa3",
  "#c44f5e",
  "#1e5f74",
  "#9f3f52",
  "#356fa8",
  "#b54a5a",
];

/** Round up to a "nice" axis ceiling (1, 2, 2.5, 5, 10 × 10ⁿ). */
const niceAxisCeil = (v) => {
  if (!Number.isFinite(v) || v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const norm = v / base;
  const nice =
    norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return nice * base;
};

/**
 * Draw a programmatic line chart of citations-by-year inside `frame`.
 * One line per entity, with grid lines, axis labels, and point markers —
 * a stripped-down PDF analogue of `CitationByYearChart`.
 */
const drawPdfCitationLineChart = (
  pdf,
  frame,
  series,
  colorMap,
  fallbackPalette = CITATION_LINE_COLORS_PDF
) => {
  const { x, y, width, height } = frame;

  const padTop = 4;
  const padBottom = 12;
  const padLeft = 18;
  const padRight = 4;

  const plotX = x + padLeft;
  const plotY = y + padTop;
  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;
  if (plotW <= 0 || plotH <= 0) return null;

  const yearSet = new Set();
  let rawMax = 0;
  series.forEach(({ points }) => {
    (points || []).forEach(({ year, value }) => {
      yearSet.add(year);
      if (Number.isFinite(value) && value > rawMax) rawMax = value;
    });
  });
  const years = Array.from(yearSet).sort((a, b) => a - b);
  if (!years.length) return null;
  const yAxisMax = niceAxisCeil(rawMax || 1);

  const xAt = (yr) => {
    if (years.length === 1) return plotX + plotW / 2;
    const minY = years[0];
    const maxY = years[years.length - 1];
    return plotX + ((yr - minY) / (maxY - minY)) * plotW;
  };
  const yAt = (v) => plotY + plotH - (v / yAxisMax) * plotH;

  // Plot border.
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.25);
  pdf.rect(plotX, plotY, plotW, plotH);

  // Y-axis grid + tick labels (5 intervals, 6 ticks).
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7.5);
  pdf.setTextColor(100, 116, 139);
  const yTickCount = 5;
  for (let i = 0; i <= yTickCount; i++) {
    const t = i / yTickCount;
    const yPos = plotY + plotH - t * plotH;
    const value = t * yAxisMax;
    if (i > 0 && i < yTickCount) {
      pdf.setDrawColor(241, 245, 249);
      pdf.line(plotX, yPos, plotX + plotW, yPos);
    }
    pdf.text(formatBenchmarkChartValue(value, 0), plotX - 1.5, yPos + 1.4, {
      align: "right",
    });
  }

  // X-axis tick labels (years), thinned out when too dense.
  const maxYearLabels = Math.max(2, Math.floor(plotW / 14));
  const yearStep = Math.max(1, Math.ceil(years.length / maxYearLabels));
  years.forEach((yr, idx) => {
    if (idx % yearStep === 0 || idx === years.length - 1) {
      pdf.text(String(yr), xAt(yr), plotY + plotH + 4, { align: "center" });
    }
  });

  // Per-entity line + dot markers.
  series.forEach(({ id, points }, sIdx) => {
    const fallback = fallbackPalette[sIdx % fallbackPalette.length];
    const color = (colorMap && colorMap[id]) || fallback;
    const rgb = hexToRgb(color);
    pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
    pdf.setLineWidth(0.7);

    let prev = null;
    (points || []).forEach(({ year, value }) => {
      if (!Number.isFinite(value)) {
        prev = null; // break the line on missing values
        return;
      }
      const px = xAt(year);
      const py = yAt(value);
      if (prev) pdf.line(prev.x, prev.y, px, py);
      pdf.circle(px, py, 0.7, "F");
      prev = { x: px, y: py };
    });
  });

  return { years, yAxisMax };
};

/**
 * Year × entity detail table that "explains" the citations-by-year chart.
 * Paginates onto follow-up pages with the same header repeated.
 */
const drawPdfCitationDetailTable = (
  pdf,
  startX,
  startY,
  contentWidth,
  series,
  years,
  entityIds,
  entityNames,
  pageHeight,
  margin,
  title = "Citations Over Time"
) => {
  const HEADER_H = 9;
  const ROW_H = 6;
  const yearColW = Math.min(22, Math.max(14, contentWidth * 0.07));
  const remaining = contentWidth - yearColW;
  const entityColW = remaining / Math.max(entityIds.length, 1);

  const drawHeader = (cy) => {
    pdf.setFillColor(15, 52, 96);
    pdf.rect(startX, cy, contentWidth, HEADER_H, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    pdf.setTextColor(255, 255, 255);
    pdf.text("YEAR", startX + yearColW / 2, cy + HEADER_H / 2 + 1.4, {
      align: "center",
    });

    let cx = startX + yearColW;
    entityIds.forEach((id) => {
      const name = entityNames[id] || id;
      const truncated = truncatePdfTextToWidth(pdf, name, entityColW - 4);
      pdf.text(truncated, cx + entityColW / 2, cy + HEADER_H / 2 + 1.4, {
        align: "center",
      });
      cx += entityColW;
    });

    // Header border.
    pdf.setDrawColor(15, 52, 96);
    pdf.setLineWidth(0.2);
    pdf.rect(startX, cy, contentWidth, HEADER_H);
  };

  // Per-series year -> value lookup for fast cell rendering.
  const lookups = series.map(({ id, points }) => {
    const m = new Map();
    (points || []).forEach((p) => m.set(p.year, p.value));
    return { id, m };
  });

  let cursorY = startY;
  // Track where the current page's table starts so the final border rect and
  // vertical dividers are anchored to the correct page (startY is from the
  // calling page and would produce a negative-height rect on continuation pages).
  let pageStartY = startY;
  drawHeader(cursorY);
  cursorY += HEADER_H;

  years.forEach((year, rowIdx) => {
    if (cursorY + ROW_H > pageHeight - margin) {
      pdf.addPage();
      drawOverviewPageHeader(
        pdf,
        `${title} (continued)`,
        pdf.internal.pageSize.getWidth()
      );
      pageStartY = margin + 14;
      cursorY = pageStartY;
      drawHeader(cursorY);
      cursorY += HEADER_H;
    }

    if (rowIdx % 2 === 1) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(startX, cursorY, contentWidth, ROW_H, "F");
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(15, 23, 42);
    pdf.text(
      String(year),
      startX + yearColW / 2,
      cursorY + ROW_H / 2 + 1.3,
      { align: "center" }
    );

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(51, 65, 85);
    let cx = startX + yearColW;
    entityIds.forEach((id) => {
      const found = lookups.find((s) => String(s.id) === String(id));
      const v = found ? found.m.get(year) : undefined;
      const text =
        v == null
          ? "—"
          : formatBenchmarkChartValue(v, 0, { abbreviate: false });
      const truncated = truncatePdfTextToWidth(pdf, text, entityColW - 4);
      pdf.text(truncated, cx + entityColW / 2, cursorY + ROW_H / 2 + 1.3, {
        align: "center",
      });
      cx += entityColW;
    });

    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(startX, cursorY + ROW_H, startX + contentWidth, cursorY + ROW_H);

    cursorY += ROW_H;
  });

  // Vertical column dividers and outer border — anchored to the last page's
  // table start (pageStartY) so they never span across page boundaries.
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.2);
  pdf.line(startX + yearColW, pageStartY, startX + yearColW, cursorY);
  for (let i = 0; i < entityIds.length - 1; i++) {
    const vx = startX + yearColW + (i + 1) * entityColW;
    pdf.line(vx, pageStartY, vx, cursorY);
  }
  pdf.rect(startX, pageStartY, contentWidth, cursorY - pageStartY);

  return cursorY;
};

/** Build per-keyword lookup maps so the breakdown table can fetch metrics in O(1). */
const buildEntityRawDataLookups = (rawData, entityIdKey) =>
  (rawData || []).map((entry) => {
    const keyword = Object.keys(entry || {})[0] || "";
    const map = {};
    (entry?.[keyword] || []).forEach((item) => {
      // Mirror `BreakdownTable` lookup: prefer the mode-specific id, fall
      // back to `institution_id` so already-normalized rawData also works.
      const id = item?.[entityIdKey] ?? item?.institution_id;
      if (id != null) {
        map[String(id)] = item;
      }
    });
    return { keyword, map };
  });

// Breakdown PDF table styling. Header is taller so 2-line institution names
// fit; metric rows are short so a 6-metric topic group stays compact.
const PDF_BREAKDOWN_HEADER_HEIGHT = 14;
const PDF_BREAKDOWN_METRIC_ROW_HEIGHT = 6.5;
const PDF_BREAKDOWN_HEADER_LINE_HEIGHT = 4;
const PDF_BREAKDOWN_TOPIC_LINE_HEIGHT = 4.4;

/**
 * Wrap text to at most `maxLines`, ellipsizing the last visible line if
 * more content remains. Returns `string[]` of visible lines.
 */
const wrapPdfText = (pdf, text, maxWidth, maxLines = 2) => {
  const original = String(text ?? "");
  if (!original) return [""];
  const lines = pdf.splitTextToSize(original, maxWidth);
  if (lines.length <= maxLines) return lines;
  const visible = lines.slice(0, maxLines);
  let last = visible[maxLines - 1];
  while (last.length > 0 && pdf.getTextWidth(last + "…") > maxWidth) {
    last = last.slice(0, -1);
  }
  visible[maxLines - 1] = last + "…";
  return visible;
};

/**
 * Header row for the institution-mode breakdown. Topic / Metric headers are
 * single-line; institution columns wrap to 2 lines so long names like
 * "Massachusetts Institute of Technology" no longer overflow into the next
 * column.
 */
const drawPdfBreakdownHeader = (pdf, x, y, columns) => {
  const totalW = columns.reduce((a, c) => a + c.width, 0);
  pdf.setFillColor(241, 245, 249);
  pdf.rect(x, y, totalW, PDF_BREAKDOWN_HEADER_HEIGHT, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  pdf.setTextColor(15, 23, 42);

  let cx = x;
  columns.forEach((c) => {
    const align = c.align || "left";
    const padding = 3;
    const innerWidth = c.width - padding * 2;
    if (c.wrap) {
      const visible = wrapPdfText(pdf, c.label, innerWidth, 2);
      const totalTextH = visible.length * PDF_BREAKDOWN_HEADER_LINE_HEIGHT;
      const startTextY =
        y + (PDF_BREAKDOWN_HEADER_HEIGHT - totalTextH) / 2 + 3;
      visible.forEach((line, idx) => {
        const tx =
          align === "right"
            ? cx + c.width - padding
            : align === "center"
              ? cx + c.width / 2
              : cx + padding;
        pdf.text(line, tx, startTextY + idx * PDF_BREAKDOWN_HEADER_LINE_HEIGHT, {
          align,
        });
      });
    } else {
      const text = truncatePdfTextToWidth(pdf, c.label, innerWidth);
      const tx =
        align === "right"
          ? cx + c.width - padding
          : align === "center"
            ? cx + c.width / 2
            : cx + padding;
      pdf.text(text, tx, y + PDF_BREAKDOWN_HEADER_HEIGHT / 2 + 1.4, { align });
    }
    cx += c.width;
  });

  pdf.setDrawColor(203, 213, 225);
  pdf.setLineWidth(0.25);
  pdf.rect(x, y, totalW, PDF_BREAKDOWN_HEADER_HEIGHT);
};

/**
 * One topic group: tall vertically-centered topic cell on the left,
 * one metric sub-row per `subCols` entry for the metric label + per-entity
 * values. Borders mimic on-screen grid lines.
 */
const drawPdfBreakdownTopicGroup = (
  pdf,
  x,
  y,
  columns,
  topicLabel,
  entityLookup,
  entityIds,
  subCols,
  isAlt
) => {
  const groupHeight = PDF_BREAKDOWN_METRIC_ROW_HEIGHT * subCols.length;
  const totalW = columns.reduce((a, c) => a + c.width, 0);
  const topicCol = columns[0];
  const metricCol = columns[1];
  const entityCols = columns.slice(2);

  if (isAlt) {
    pdf.setFillColor(248, 250, 252);
    pdf.rect(x, y, totalW, groupHeight, "F");
  }

  // Merged topic cell: horizontally + vertically centered text (up to 3 lines).
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  pdf.setTextColor(15, 23, 42);
  const topicLines = wrapPdfText(pdf, topicLabel, topicCol.width - 6, 3);
  const topicTextH = topicLines.length * PDF_BREAKDOWN_TOPIC_LINE_HEIGHT;
  const topicStartY = y + (groupHeight - topicTextH) / 2 + 3;
  topicLines.forEach((line, idx) => {
    pdf.text(
      line,
      x + topicCol.width / 2,
      topicStartY + idx * PDF_BREAKDOWN_TOPIC_LINE_HEIGHT,
      { align: "center" }
    );
  });

  // Metric label + entity values per sub-row.
  let rowY = y;
  subCols.forEach((subCol) => {
    const cellY = rowY + PDF_BREAKDOWN_METRIC_ROW_HEIGHT / 2 + 1.3;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    pdf.setTextColor(71, 85, 105);
    pdf.text(
      formatBenchmarkMetricShortLabel(subCol.label).toUpperCase(),
      x + topicCol.width + metricCol.width / 2,
      cellY,
      { align: "center" }
    );

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(51, 65, 85);

    let entityColX = x + topicCol.width + metricCol.width;
    entityIds.forEach((id, idx) => {
      const item = entityLookup[String(id)];
      const v = item?.[subCol.key];
      const text =
        v == null
          ? "—"
          : formatBenchmarkChartValue(v, subCol.decimals ?? 0, {
              abbreviate: false,
            });
      const colWidth = entityCols[idx].width;
      const truncated = truncatePdfTextToWidth(pdf, text, colWidth - 6);
      pdf.text(truncated, entityColX + colWidth / 2, cellY, {
        align: "center",
      });
      entityColX += colWidth;
    });

    rowY += PDF_BREAKDOWN_METRIC_ROW_HEIGHT;
  });

  // Outer border + column dividers + horizontal lines between metric rows.
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.2);
  pdf.rect(x, y, totalW, groupHeight);

  let vx = x + topicCol.width;
  pdf.line(vx, y, vx, y + groupHeight);
  vx += metricCol.width;
  pdf.line(vx, y, vx, y + groupHeight);
  for (let i = 0; i < entityCols.length - 1; i++) {
    vx += entityCols[i].width;
    pdf.line(vx, y, vx, y + groupHeight);
  }

  for (let i = 1; i < subCols.length; i++) {
    const hy = y + i * PDF_BREAKDOWN_METRIC_ROW_HEIGHT;
    pdf.line(x + topicCol.width, hy, x + totalW, hy);
  }
};

const titleCase = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

/**
 * Render a section page header: bold title, optional muted subtitle lines
 * (string or string[] — each rendered on its own line, truncated to fit),
 * and a navy rule. Returns the Y coordinate after the rule so callers can
 * position content without re-computing the header height.
 */
const drawPdfPairPageHeader = (pdf, title, subtitle, pageWidth) => {
  const M = INSTITUTION_PDF_MARGIN;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(15, 23, 42);
  pdf.text(title, M, M + 5);

  const lines = Array.isArray(subtitle)
    ? subtitle.filter(Boolean)
    : subtitle
    ? [subtitle]
    : [];

  let lineY = M + 8;
  if (lines.length > 0) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(100, 116, 139);
    const maxW = pageWidth - M * 2;
    lines.forEach((ln, i) => {
      pdf.text(truncatePdfTextToWidth(pdf, ln, maxW), M, M + 10 + i * 4.5);
    });
    lineY = M + 10 + lines.length * 4.5;
  }

  pdf.setDrawColor(15, 52, 96);
  pdf.setLineWidth(0.6);
  pdf.line(M, lineY, pageWidth - M, lineY);

  return lineY + 4;
};

/**
 * Draw one grouped-bar tile: one vertical bar per entity, coloured by
 * colorMap, with the formatted value annotated above each bar.
 * Matches the visual style of `drawPdfPieTile` (soft card background, uppercase
 * label, muted Total subtitle).
 */
const drawPdfBarTile = (pdf, bounds, stat, institutions, colorMap) => {
  const { x, y, width, height } = bounds;

  pdf.setFillColor(248, 250, 252);
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(x + 1, y + 1, width - 2, height - 2, 1.8, 1.8, "FD");

  const totalValue = institutions.reduce(
    (sum, inst) => sum + (Number(inst[stat.key]) || 0),
    0
  );
  const maxValue = institutions.reduce(
    (mx, inst) => Math.max(mx, Number(inst[stat.key]) || 0),
    0
  );

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(71, 85, 105);
  pdf.text(
    formatBenchmarkMetricShortLabel(stat.label).toUpperCase(),
    x + width / 2,
    y + 6,
    { align: "center" }
  );

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(148, 163, 184);
  pdf.text(
    `Total: ${formatBenchmarkChartValue(totalValue, stat.decimals ?? 0)}`,
    x + width / 2,
    y + 10,
    { align: "center" }
  );

  if (maxValue <= 0) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor(148, 163, 184);
    pdf.text("No data", x + width / 2, y + height / 2 + 2, { align: "center" });
    return;
  }

  // Plot area: room for title+total at top (14 mm), value annotations (5 mm
  // headroom above bars), and a thin baseline at the bottom.
  const padTop = 14;
  const annotHeadroom = 5;
  const padSide = 4;
  const plotX = x + padSide;
  const plotW = width - padSide * 2;
  const plotBottom = y + height - 3;
  const plotTop = y + padTop + annotHeadroom;
  const plotH = plotBottom - plotTop;

  if (plotH <= 2 || plotW <= 2) return;

  const n = institutions.length;
  const groupW = plotW / n;
  const barGap = Math.max(0.5, groupW * 0.15);
  const barW = Math.max(1, groupW - barGap * 2);

  institutions.forEach((inst, i) => {
    const value = Number(inst[stat.key]) || 0;
    const barH = (value / maxValue) * plotH;
    const barX = plotX + i * groupW + barGap;
    const barY = plotBottom - barH;

    const rgb = hexToRgb(colorMap[inst.institution_id] || BENCHMARK_INSTITUTION_COLORS[0]);
    pdf.setFillColor(rgb[0], rgb[1], rgb[2]);

    if (barH > 0.5) {
      pdf.rect(barX, barY, barW, barH, "F");
    }

    if (value > 0) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(6);
      pdf.setTextColor(30, 41, 59);
      pdf.text(
        formatBenchmarkChartValue(value, stat.decimals ?? 0),
        barX + barW / 2,
        barY - 0.8,
        { align: "center" }
      );
    }
  });

  pdf.setDrawColor(203, 213, 225);
  pdf.setLineWidth(0.4);
  pdf.line(plotX, plotBottom, plotX + plotW, plotBottom);
};

/** "20260512_143022" — safe for all filesystems. */
const fileTimestamp = () => {
  const d = new Date();
  const pad = (n, len = 2) => String(n).padStart(len, "0");
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
};

const defaultPairFilename = (mode, ext) => {
  const slug =
    mode === BENCHMARK_MODES.RESEARCHER
      ? "researcher"
      : mode === BENCHMARK_MODES.UNIT
        ? "unit"
        : mode === BENCHMARK_MODES.VENUE
          ? "venue"
          : "institution";
  return `${slug}_benchmark_${fileTimestamp()}.${ext}`;
};

/**
 * Programmatic landscape-A4 PDF for the four "Benchmark by ..." modes.
 *
 * Page structure:
 *   1. Summary   — topics list · entity list · aggregated metric table
 *   2. Bar chart — grouped bars per entity, one tile per metric, value annotations
 *   3. Pie chart — donut slices with value annotations
 *   4. Citations over time — line chart + legend (all modes)
 *   5. Citation detail table — year × entity
 *   6+. Research Topics Specific Breakdown
 *
 * Every section header includes a subtitle line with the selected topics and
 * entity names so each page is self-contained when printed or shared.
 */
export const exportBenchmarkPairPdf = ({
  mode = BENCHMARK_MODES.INSTITUTION,
  selectedKeywords = [],
  entityIds = [],
  entityNames = {},
  rawData = [],
  filename,
} = {}) => {
  const entityIdKey = ENTITY_ID_KEY_BY_MODE[mode] || "institution_id";
  const entityLabel =
    ENTITY_LABEL_BY_MODE[mode] || ENTITY_LABEL_BY_MODE[BENCHMARK_MODES.INSTITUTION];
  const resolvedFilename = filename || defaultPairFilename(mode, "pdf");

  const visiblePieStats = filterMetricsByMode(BENCHMARK_PIE_STATS, mode);
  const visibleBarStats = filterMetricsByMode(BENCHMARK_BAR_STATS, mode);
  const visibleSubCols = filterMetricsByMode(BENCHMARK_SUB_COLS, mode);

  const normalisedRawData =
    mode !== BENCHMARK_MODES.INSTITUTION
      ? normalizeRawDataForCharts(rawData, entityIdKey)
      : rawData;

  const pdf = new jsPDF("landscape", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - INSTITUTION_PDF_MARGIN * 2;
  const M = INSTITUTION_PDF_MARGIN;

  const colorMap = {};
  entityIds.forEach((id, idx) => {
    colorMap[id] =
      BENCHMARK_INSTITUTION_COLORS[idx % BENCHMARK_INSTITUTION_COLORS.length];
  });

  const aggregated = aggregateBenchmarkByInstitution(
    normalisedRawData,
    entityIds.map(String)
  );
  const orderedEntities = entityIds
    .map((id) =>
      aggregated.find((inst) => String(inst.institution_id) === String(id))
    )
    .filter(Boolean);

  // Two-line subtitle shown on every section header so each page is self-contained.
  const kwLabel = selectedKeywords.length
    ? selectedKeywords.map((kw) => overviewCapitalize(kw)).join(", ")
    : "—";
  const entityListLabel = entityIds.length
    ? entityIds.map((id) => entityNames[id] || id).join(", ")
    : "—";
  const pageSubtitle = [
    `Topics: ${kwLabel}`,
    `${titleCase(entityLabel.plural)}: ${entityListLabel}`,
  ];

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.setTextColor(15, 23, 42);
  pdf.text(
    `${titleCase(entityLabel.singular)} Benchmark Report`,
    M,
    M + 4
  );

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(100, 116, 139);
  pdf.text(`Generated ${new Date().toLocaleString()}`, M, M + 11);

  pdf.setDrawColor(15, 52, 96);
  pdf.setLineWidth(0.8);
  pdf.line(M, M + 14, pageWidth - M, M + 14);

  let y = M + 22;
  const leftColW = contentWidth * 0.42;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(15, 23, 42);
  pdf.text("Selected Research Topics", M, y);
  y += 6;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(51, 65, 85);
  if (!selectedKeywords.length) {
    pdf.setTextColor(148, 163, 184);
    pdf.text("(none selected)", M + 4, y);
    y += 6;
    pdf.setTextColor(51, 65, 85);
  } else {
    selectedKeywords.forEach((kw) => {
      pdf.splitTextToSize(`• ${overviewCapitalize(kw)}`, leftColW - 4).forEach(
        (line) => {
          if (y > pageHeight - M) { pdf.addPage(); y = M + 6; }
          pdf.text(line, M + 4, y);
          y += 5.5;
        }
      );
    });
  }

  y += 4;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(15, 23, 42);
  pdf.text(`Selected ${titleCase(entityLabel.plural)}`, M, y);
  y += 6;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(51, 65, 85);
  if (!entityIds.length) {
    pdf.setTextColor(148, 163, 184);
    pdf.text("(none selected)", M + 4, y);
    pdf.setTextColor(51, 65, 85);
  } else {
    entityIds.forEach((id) => {
      pdf.splitTextToSize(`• ${entityNames[id] || id}`, leftColW - 4).forEach(
        (line) => {
          if (y > pageHeight - M) return;
          pdf.text(line, M + 4, y);
          y += 5.5;
        }
      );
    });
  }

  // ── Page 2: Bar chart ────────────────────────────────────────────────────
  pdf.addPage();
  const barPageContentTop = drawPdfPairPageHeader(
    pdf,
    `Inter-${titleCase(entityLabel.singular)} Metric Scaling`,
    pageSubtitle,
    pageWidth
  );

  const barLegendBottomY = drawPdfInstitutionLegend(
    pdf,
    M,
    barPageContentTop,
    contentWidth,
    entityIds,
    entityNames,
    colorMap
  );

  const barStats = visibleBarStats.filter((stat) =>
    orderedEntities.some((inst) => (Number(inst[stat.key]) || 0) > 0)
  );

  if (barStats.length > 0) {
    const barCols = barStats.length <= 3 ? Math.max(barStats.length, 1) : 3;
    const barRows = Math.ceil(barStats.length / barCols);
    const barGridTop = barLegendBottomY + 2;
    const barCellWidth = contentWidth / barCols;
    const barCellHeight =
      (pageHeight - M - 2 - barGridTop) / barRows;

    barStats.forEach((stat, idx) => {
      const rowIdx = Math.floor(idx / barCols);
      const colIdx = idx % barCols;
      drawPdfBarTile(
        pdf,
        {
          x: M + colIdx * barCellWidth,
          y: barGridTop + rowIdx * barCellHeight,
          width: barCellWidth,
          height: barCellHeight,
        },
        stat,
        orderedEntities,
        colorMap
      );
    });
  }

  // ── Page 3: Pie chart ────────────────────────────────────────────────────
  pdf.addPage();
  const piePageContentTop = drawPdfPairPageHeader(
    pdf,
    "Proportional Distribution",
    pageSubtitle,
    pageWidth
  );

  const pieLegendBottomY = drawPdfInstitutionLegend(
    pdf,
    M,
    piePageContentTop,
    contentWidth,
    entityIds,
    entityNames,
    colorMap
  );

  const pieStatsFiltered = visiblePieStats.filter((stat) =>
    orderedEntities.some((inst) => (Number(inst[stat.key]) || 0) > 0)
  );
  const pieCols = pieStatsFiltered.length <= 3
    ? Math.max(pieStatsFiltered.length, 1)
    : 3;
  const pieRows = Math.ceil(pieStatsFiltered.length / pieCols);
  const pieGridTop = pieLegendBottomY + 2;
  const pieCellWidth = contentWidth / pieCols;
  const pieCellHeight = (pageHeight - M - 2 - pieGridTop) / pieRows;

  pieStatsFiltered.forEach((stat, idx) => {
    const rowIdx = Math.floor(idx / pieCols);
    const colIdx = idx % pieCols;
    drawPdfPieTile(
      pdf,
      {
        x: M + colIdx * pieCellWidth,
        y: pieGridTop + rowIdx * pieCellHeight,
        width: pieCellWidth,
        height: pieCellHeight,
      },
      stat,
      orderedEntities,
      colorMap
    );
  });

  // ── Pages 4+: Publication / Grants Over Time ────────────────────────────
  const renderYearlyMetricPage = (series, sectionTitle) => {
    if (!series.length) return;
    pdf.addPage();
    const contentTop = drawPdfPairPageHeader(
      pdf,
      sectionTitle,
      pageSubtitle,
      pageWidth
    );
    const chartHeight = 70;
    drawPdfCitationLineChart(
      pdf,
      { x: M, y: contentTop, width: contentWidth, height: chartHeight },
      series,
      colorMap,
      CITATION_LINE_COLORS_PDF
    );
    const legendBottomY = drawPdfInstitutionLegend(
      pdf,
      M,
      contentTop + chartHeight + 6,
      contentWidth,
      entityIds,
      entityNames,
      colorMap
    );
    const allYears = (() => {
      const set = new Set();
      series.forEach(({ points }) =>
        (points || []).forEach((p) => set.add(p.year))
      );
      return Array.from(set).sort((a, b) => a - b);
    })();
    drawPdfCitationDetailTable(
      pdf,
      M,
      legendBottomY + 4,
      contentWidth,
      series,
      allYears,
      entityIds,
      entityNames,
      pageHeight,
      M,
      sectionTitle
    );
  };

  const publicationSeries = collectMetricByYearSeries(
    rawData,
    entityIds,
    entityIdKey,
    "publication_count_by_year"
  );
  renderYearlyMetricPage(publicationSeries, "Publications Over Time");

  const isVenue = mode === BENCHMARK_MODES.VENUE;
  if (!isVenue) {
    const grantsSeries = collectMetricByYearSeries(
      rawData,
      entityIds,
      entityIdKey,
      "grants_count_by_year"
    );
    renderYearlyMetricPage(grantsSeries, "Grants Over Time");
  }

  // ── Research Topics Specific Breakdown ───────────────────────────────────
  if (rawData?.length) {
    const lookups = buildEntityRawDataLookups(rawData, entityIdKey);

    const TOPIC_COL = 42;
    const METRIC_COL = 36;
    const remainingWidth = contentWidth - TOPIC_COL - METRIC_COL;
    const entityColWidth = Math.max(
      18,
      remainingWidth / Math.max(entityIds.length, 1)
    );
    const breakdownColumns = [
      { label: "RESEARCH TOPIC", width: TOPIC_COL, align: "center" },
      { label: "METRIC", width: METRIC_COL, align: "center" },
      ...entityIds.map((id) => ({
        label: entityNames[id] || id,
        width: entityColWidth,
        align: "center",
        wrap: true,
      })),
    ];

    pdf.addPage();
    let breakdownContentTop = drawPdfPairPageHeader(
      pdf,
      "Research Topics Specific Breakdown",
      pageSubtitle,
      pageWidth
    );

    drawPdfBreakdownHeader(pdf, M, breakdownContentTop, breakdownColumns);
    let cursorY = breakdownContentTop + PDF_BREAKDOWN_HEADER_HEIGHT;

    const groupHeight = PDF_BREAKDOWN_METRIC_ROW_HEIGHT * visibleSubCols.length;

    lookups.forEach(({ keyword, map }, topicIdx) => {
      if (cursorY + groupHeight > pageHeight - M) {
        pdf.addPage();
        const contTop = drawPdfPairPageHeader(
          pdf,
          "Research Topics Specific Breakdown (continued)",
          pageSubtitle,
          pageWidth
        );
        drawPdfBreakdownHeader(pdf, M, contTop, breakdownColumns);
        cursorY = contTop + PDF_BREAKDOWN_HEADER_HEIGHT;
      }

      drawPdfBreakdownTopicGroup(
        pdf,
        M,
        cursorY,
        breakdownColumns,
        overviewCapitalize(keyword || "—"),
        map,
        entityIds,
        visibleSubCols,
        topicIdx % 2 === 1
      );
      cursorY += groupHeight;
    });
  }

  pdf.save(resolvedFilename);
};

export const exportBenchmarkPairBreakdownExcel = async ({
  mode = BENCHMARK_MODES.INSTITUTION,
  rawData = [],
  entityIds = [],
  entityNames = {},
  filename,
} = {}) => {
  const isInstitution = mode === BENCHMARK_MODES.INSTITUTION;
  const entityIdKey = ENTITY_ID_KEY_BY_MODE[mode] || "institution_id";
  const visibleSubCols = filterMetricsByMode(BENCHMARK_SUB_COLS, mode);
  const resolvedFilename =
    filename || defaultPairFilename(mode, "xlsx").replace(
      ".xlsx",
      "_breakdown.xlsx"
    );

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Research Topics Breakdown");
  const lookups = buildEntityRawDataLookups(rawData, entityIdKey);
  const subColCount = visibleSubCols.length;
  const totalCols = 2 + entityIds.length;

  // Header row.
  sheet.getCell(1, 1).value = "Research Topic";
  sheet.getCell(1, 2).value = "Metric";
  entityIds.forEach((id, idx) => {
    sheet.getCell(1, 3 + idx).value = entityNames[id] || id;
  });
  styleHeaderRow(sheet, 1, 1, totalCols);

  // Data rows.
  let row = 2;
  const topicRanges = [];
  lookups.forEach(({ keyword, map }) => {
    const topicLabel = overviewCapitalize(keyword || "—");
    const topicStart = row;

    visibleSubCols.forEach((subCol, metricIdx) => {
      sheet.getCell(row, 1).value = metricIdx === 0 ? topicLabel : null;
      sheet.getCell(row, 2).value = subCol.label;

      entityIds.forEach((id, entityIdx) => {
        const item = map[String(id)];
        const v = item?.[subCol.key];
        const cell = sheet.getCell(row, 3 + entityIdx);
        if (v == null || Number.isNaN(Number(v))) {
          cell.value = null;
        } else {
          cell.value = Number(v);
          cell.numFmt = (subCol.decimals ?? 0) > 0 ? "#,##0.00" : "#,##0";
          cell.alignment = { horizontal: "right" };
        }
      });

      row += 1;
    });

    topicRanges.push({ start: topicStart, end: topicStart + subColCount - 1 });
  });

  // Vertically merge topic cells across their metric rows + style.
  topicRanges.forEach(({ start, end }) => {
    if (end > start) sheet.mergeCells(start, 1, end, 1);
    const topicCell = sheet.getCell(start, 1);
    topicCell.font = { bold: true };
    topicCell.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };
  });

  if (row > 2) {
    applyBorders(sheet, 1, row - 1, 1, totalCols);
  }

  sheet.getColumn(1).width = 28;
  sheet.getColumn(2).width = 22;
  for (let c = 3; c <= totalCols; c++) {
    sheet.getColumn(c).width = 18;
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, resolvedFilename);
};

// ─── User-curated report ("Add to report" → PDF) ───────────────────────────

/** Colour-swatch legend row(s); wraps within `contentW`. Returns the new y. */
const drawReportLegend = (pdf, legend, startY, contentW) => {
  if (!legend || !legend.length) return startY;
  const M = PDF_MARGIN_MM;
  const swatch = 3.2;
  const rowH = 5.4;
  let x = M;
  let y = startY;
  pdf.setFont("times", "normal");
  pdf.setFontSize(8.5);
  legend.forEach((item) => {
    const label = String(item.name || "");
    const textW = pdf.getTextWidth(label);
    const itemW = swatch + 1.8 + textW + 6;
    if (x + itemW > M + contentW) {
      x = M;
      y += rowH;
    }
    const [r, g, b] = hexToRgb(item.color);
    pdf.setFillColor(r, g, b);
    pdf.roundedRect(x, y - swatch + 0.6, swatch, swatch, 0.6, 0.6, "F");
    pdf.setTextColor(71, 85, 105);
    pdf.text(label, x + swatch + 1.8, y);
    x += itemW;
  });
  return y + rowH;
};

const drawReportTable = (pdf, table, startY, contentW, pageH) => {
  if (!table || !table.columns?.length || !table.rows?.length) return startY;
  const M = PDF_MARGIN_MM;
  const cols = table.columns;
  const nCols = cols.length;

  // Column widths from explicit weights, else first column slightly wider.
  let widths;
  if (Array.isArray(table.colWidths) && table.colWidths.length === nCols) {
    const total = table.colWidths.reduce((a, b) => a + b, 0) || 1;
    widths = table.colWidths.map((w) => (w / total) * contentW);
  } else {
    const firstW = Math.min(contentW * 0.3, 58);
    const restW = nCols > 1 ? (contentW - firstW) / (nCols - 1) : contentW;
    widths = cols.map((_, i) => (i === 0 ? firstW : restW));
  }
  const colX = [M];
  for (let i = 1; i < nCols; i += 1) colX.push(colX[i - 1] + widths[i - 1]);

  const pad = 1.8;
  const lineH = 3.8;
  const fontSize = 8;
  let y = startY;

  const linesFor = (text, i) => {
    pdf.setFontSize(fontSize);
    const s = text == null ? "—" : String(text);
    return pdf.splitTextToSize(s === "" ? " " : s, widths[i] - pad * 2);
  };

  const paintRow = (cells, bold) => {
    const perCol = cells.map((c, i) => linesFor(c, i));
    const maxLines = Math.max(1, ...perCol.map((l) => l.length));
    const rowH = maxLines * lineH + 3;
    if (bold) {
      pdf.setFillColor(241, 245, 249);
      pdf.rect(M, y, contentW, rowH, "F");
    }
    pdf.setFont("times", bold ? "bold" : "normal");
    pdf.setFontSize(fontSize);
    pdf.setTextColor(bold ? 51 : 71, bold ? 65 : 85, bold ? 85 : 105);
    perCol.forEach((lines, i) => {
      const leftAlign = i === 0;
      const tx = leftAlign ? colX[i] + pad : colX[i] + widths[i] - pad;
      lines.forEach((ln, li) => {
        const ty = y + 2 + (li + 1) * lineH - 0.9;
        if (leftAlign) pdf.text(ln, tx, ty);
        else pdf.text(ln, tx, ty, { align: "right" });
      });
    });
    if (!bold) {
      pdf.setDrawColor(226, 232, 240);
      pdf.line(M, y + rowH, M + contentW, y + rowH);
    }
    y += rowH;
  };

  const drawHeader = () => paintRow(cols, true);

  drawHeader();
  table.rows.forEach((row) => {
    const needed =
      Math.max(1, ...row.map((c, i) => linesFor(c, i).length)) * lineH + 3;
    if (y + needed > pageH - M) {
      pdf.addPage();
      y = M;
      drawHeader();
    }
    paintRow(row, false);
  });
  return y + 4;
};


export const captureReportItems = async (items = []) => {
  const captures = [];
  for (const item of items) {
    const el = document.querySelector(`[data-report-id="${item.id}"]`);
    if (!el) continue;
    // eslint-disable-next-line no-await-in-loop
    const canvas = await html2canvas(el, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      ignoreElements: (node) =>
        node?.getAttribute?.("data-report-ignore") === "true",
      // Hide on-chart titles/headers (and breakdown logos/scroll hint) in the
      // snapshot — the PDF draws a single, consistently-spaced title itself.
      onclone: (clonedDoc) => {
        clonedDoc
          .querySelectorAll('[data-report-strip="true"]')
          .forEach((node) => {
            node.style.display = "none";
          });
      },
    });
    captures.push({
      id: item.id,
      title: item.title,
      dataUrl: canvas.toDataURL("image/png"),
      width: canvas.width,
      height: canvas.height,
    });
  }
  return captures;
};


const EXPORT_FONT_STACK = "Arial, 'Helvetica Neue', Helvetica, sans-serif";
const EXPORT_FONT_IMPORTANT = `font-family:${EXPORT_FONT_STACK} !important;`;
const TABLE_RENDER_WIDTH = 1500;

const drawScaled = (src, w, h) => {
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(w));
  out.height = Math.max(1, Math.round(h));
  const ctx = out.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, 0, 0, out.width, out.height);
  return out;
};

const resizeCanvasToWidth = (src, targetWidthPx, targetHeightPx) => {
  if (!targetWidthPx || !src.width || src.width === targetWidthPx) return src;
  return drawScaled(src, targetWidthPx, targetHeightPx || src.height * (targetWidthPx / src.width));
};

export const captureReportElement = async (reportId, { targetWidthPx, targetHeightPx } = {}) => {
  console.log('vaooooo: ', reportId, targetWidthPx)
  const el = document.querySelector(`[data-report-id="${reportId}"]`);
  if (!el) return null;
  // Capture the chart exactly as it is already rendered on screen (fully
  // settled — no width change, so Recharts/ECharts don't re-run their reveal
  // animation, which is what was clipping/blurring the lines). SVG charts stay
  // crisp when scaled up; we scale so the output reaches the anchor width.
  const cssWidth = el.getBoundingClientRect().width || el.offsetWidth || 0;
  let scale = 2;
  if (targetWidthPx && cssWidth) {
    scale = Math.min(6, Math.max(2, targetWidthPx / cssWidth));
  }
  const canvas = await html2canvas(el, {
    scale,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    ignoreElements: (node) =>
      node?.getAttribute?.("data-report-ignore") === "true",
    onclone: (clonedDoc) => {
      clonedDoc
        .querySelectorAll('[data-report-strip="true"]')
        .forEach((node) => {
          node.style.display = "none";
        });
    },
  });
  const out = resizeCanvasToWidth(canvas, targetWidthPx, targetHeightPx);
  return new Promise((resolve) => out.toBlob(resolve, "image/jpeg"));
};

/**
 * Render a React element OFF-SCREEN at an exact pixel box and snapshot it.
 * Because it's a fresh, hidden instance sized to `widthPx × heightPx`, the
 * chart genuinely lays out at that size (no stretching), independent of the
 * browser window, and the on-screen UI is never touched. Pass chart elements
 * with animation disabled so nothing is captured mid-reveal.
 */
export const captureReactAtSize = async (
  element,
  { widthPx, heightPx, pixelRatio = 2, settleMs = 450 } = {}
) => {
  if (!element || !widthPx) return null;
  const cssW = Math.max(1, Math.round(widthPx / pixelRatio));
  const cssH = heightPx ? Math.max(1, Math.round(heightPx / pixelRatio)) : undefined;

  const host = document.createElement("div");
  host.style.cssText =
    `position:fixed;left:-100000px;top:0;width:${cssW}px;` +
    (cssH ? `height:${cssH}px;` : "") +
    `background:#ffffff;overflow:hidden;z-index:-1;`;
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(element);
  // Let React paint, then chart libs (ECharts init / Recharts layout) settle.
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  await new Promise((r) => setTimeout(r, settleMs));

  let blob = null;
  try {
    const canvas = await html2canvas(host, {
      scale: pixelRatio,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      width: cssW,
      height: cssH,
      windowWidth: Math.max(cssW + 100, 1440),
      onclone: (doc) => {
        doc
          .querySelectorAll('[data-report-strip="true"]')
          .forEach((n) => {
            n.style.display = "none";
          });
      },
    });
    // Natural-height captures (tables): force the output width to exactly the
    // anchor width so it fills the placeholder (charts keep their exact box).
    const out = cssH ? canvas : resizeCanvasToWidth(canvas, widthPx);
    blob = await new Promise((res) => out.toBlob(res, "image/jpeg", 0.92));
  } finally {
    try {
      root.unmount();
    } catch (e) {
      /* ignore */
    }
    host.remove();
  }
  return blob;
};

export const captureTablePng = async (table, { targetWidthPx } = {}) => {
  if (!table?.columns?.length || !table?.rows?.length) return null;

  // Render directly at the anchor width (2× capture) so the image WIDTH matches
  // the placeholder exactly and the height is the table's natural height (it may
  // exceed the placeholder height — the table is never squished to fit). Font
  // and padding scale with width so every table keeps the same look.
  const PR = 2;
  const hostWidth = targetWidthPx
    ? Math.round(targetWidthPx / PR)
    : TABLE_RENDER_WIDTH;
  const k = hostWidth / TABLE_RENDER_WIDTH;
  const fs = Math.round(22 * k);
  const thPad = `${Math.round(14 * k)}px ${Math.round(18 * k)}px`;
  const tdPad = `${Math.round(12 * k)}px ${Math.round(18 * k)}px`;
  const hBorder = Math.max(1, Math.round(3 * k));

  const host = document.createElement("div");
  host.style.cssText =
    `position:fixed;left:-100000px;top:0;width:${hostWidth}px;box-sizing:border-box;` +
    `background:#ffffff;padding:0;${EXPORT_FONT_IMPORTANT}`;

  const tbl = document.createElement("table");
  tbl.style.cssText =
    `border-collapse:collapse;width:100%;font-size:${fs}px;color:#475569;${EXPORT_FONT_IMPORTANT}`;

  const thead = document.createElement("thead");
  const htr = document.createElement("tr");
  table.columns.forEach((col, i) => {
    const th = document.createElement("th");
    th.textContent = String(col);
    th.style.cssText =
      `padding:${thPad};border-bottom:${hBorder}px solid #e2e8f0;background:#f8fafc;` +
      `font-size:${fs}px;font-weight:700;color:#334155;text-align:` +
      (i === 0 ? "left" : "right") + ";" + EXPORT_FONT_IMPORTANT;
    htr.appendChild(th);
  });
  thead.appendChild(htr);
  tbl.appendChild(thead);

  const tbody = document.createElement("tbody");
  table.rows.forEach((row) => {
    const tr = document.createElement("tr");
    row.forEach((cell, i) => {
      const td = document.createElement("td");
      td.textContent = cell == null || cell === "" ? "—" : String(cell);
      td.style.cssText =
        `padding:${tdPad};border-bottom:1px solid #eef2f6;font-size:${fs}px;text-align:` +
        (i === 0 ? "left" : "right") + ";" + EXPORT_FONT_IMPORTANT;
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  tbl.appendChild(tbody);
  host.appendChild(tbl);
  document.body.appendChild(host);

  try {
    const canvas = await html2canvas(host, {
      scale: PR,
      backgroundColor: "#ffffff",
      logging: false,
    });
    // Width == anchor width; height stays natural (never constrained).
    const out = resizeCanvasToWidth(canvas, targetWidthPx);
    return await new Promise((resolve) => out.toBlob(resolve, "image/jpeg"));
  } finally {
    document.body.removeChild(host);
  }
};

/**
 * Render a "Top faculty" card (keyword title + a row of avatar + name per
 * faculty, like the on-screen section) to a PNG Blob offscreen. Avatars use the
 * real photo when it is CORS-loadable, else fall back to initials — so the
 * capture never taints the canvas. Width matches `targetWidthPx`; height is
 * dynamic.
 */
export const captureFacultyCardPng = async ({
  title,
  faculty = [],
  targetWidthPx,
  targetHeightPx,
} = {}) => {
  if (!faculty.length) return null;

  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;left:-100000px;top:0;width:1040px;background:#ffffff;" +
    `padding:32px 38px;${EXPORT_FONT_IMPORTANT}`;

  if (title) {
    const h = document.createElement("div");
    h.textContent = title;
    h.style.cssText =
      "font-size:30px;font-weight:700;color:#1e293b;margin-bottom:30px;" +
      EXPORT_FONT_IMPORTANT;
    host.appendChild(h);
  }

  const initialsOf = (name) =>
    String(name || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";

  // Faculty photos come from mixed hosts; most don't send CORS headers, so
  // html2canvas can't include them (a cross-origin image taints the canvas and
  // blocks PNG export). Route them through a CORS-enabled image proxy so the
  // avatars match the on-screen UI. Anything that still fails → initials.
  const corsPhoto = (url) => {
    if (!url || /^data:/i.test(url)) return url;
    const stripped = String(url).replace(/^https?:\/\//i, "");
    return (
      "https://images.weserv.nl/?url=" +
      encodeURIComponent(stripped) +
      "&w=220&h=220&fit=cover&output=png"
    );
  };

  // Two-column grid, row-major (1 2 / 3 4 / … / 11 12).
  const grid = document.createElement("div");
  grid.style.cssText =
    "display:grid;grid-template-columns:1fr 1fr;gap:26px 48px;";
  host.appendChild(grid);

  const waits = [];
  faculty.slice(0, 12).forEach((f) => {
    const row = document.createElement("div");
    row.style.cssText =
      "display:flex;align-items:center;gap:18px;min-width:0;";
    const av = document.createElement("div");
    av.style.cssText =
      "width:74px;height:74px;border-radius:50%;overflow:hidden;flex-shrink:0;" +
      "background:#eef2f6;display:flex;align-items:center;justify-content:center;" +
      "color:#64748b;font-weight:700;font-size:24px;" + EXPORT_FONT_IMPORTANT;
    if (f.photo_url) {
      const img = document.createElement("img");
      img.crossOrigin = "anonymous";
      img.style.cssText = "width:100%;height:100%;object-fit:cover;";
      av.appendChild(img);
      waits.push(
        new Promise((res) => {
          let done = false;
          const finish = (ok) => {
            if (done) return;
            done = true;
            if (!ok) {
              av.innerHTML = "";
              av.textContent = initialsOf(f.name);
            }
            res();
          };
          img.onload = () => finish(true);
          img.onerror = () => finish(false);
          img.src = corsPhoto(f.photo_url);
          setTimeout(() => finish(false), 7000);
        })
      );
    } else {
      av.textContent = initialsOf(f.name);
    }
    const name = document.createElement("div");
    name.textContent = f.name || "—";
    name.style.cssText =
      "font-size:27px;color:#334155;white-space:nowrap;overflow:hidden;" +
      "text-overflow:ellipsis;min-width:0;" + EXPORT_FONT_IMPORTANT;
    row.appendChild(av);
    row.appendChild(name);
    grid.appendChild(row);
  });

  document.body.appendChild(host);
  try {
    await Promise.all(waits);
    const cssWidth = host.getBoundingClientRect().width || 520;
    let scale = 2;
    if (targetWidthPx && cssWidth) {
      scale = Math.min(5, Math.max(2, targetWidthPx / cssWidth));
    }
    const canvas = await html2canvas(host, {
      scale,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
    const out = resizeCanvasToWidth(canvas, targetWidthPx);
    return await new Promise((resolve) => out.toBlob(resolve, "image/jpeg"));
  } catch (e) {
    return null;
  } finally {
    document.body.removeChild(host);
  }
};

/**
 * Render a single-metric horizontal bar chart (one bar per entity, with value
 * labels) to a PNG Blob offscreen — used for the per-metric PPTX placeholders
 * (H_INDEX, H5_INDEX, …). `entities` = [{ name, value, display, color }].
 * Fills the placeholder width; height is proportional.
 */
export const captureMetricBarPng = async ({
  title,
  entities = [],
  targetWidthPx,
} = {}) => {
  if (!entities.length) return null;

  const host = document.createElement("div");
  host.style.cssText =
    `position:fixed;left:-100000px;top:0;width:${TABLE_RENDER_WIDTH}px;box-sizing:border-box;` +
    `background:#ffffff;padding:26px 30px;${EXPORT_FONT_IMPORTANT}`;

  if (title) {
    const h = document.createElement("div");
    h.textContent = title;
    h.style.cssText =
      "font-size:24px;font-weight:700;color:#1e293b;margin-bottom:24px;" +
      EXPORT_FONT_IMPORTANT;
    host.appendChild(h);
  }

  const maxAbs = Math.max(
    1,
    ...entities.map((e) => Math.abs(Number(e.value) || 0))
  );

  entities.forEach((e) => {
    const row = document.createElement("div");
    row.style.cssText =
      "display:flex;align-items:center;gap:18px;margin-bottom:18px;";

    const label = document.createElement("div");
    label.textContent = e.name;
    label.style.cssText =
      "width:32%;font-size:19px;color:#334155;white-space:nowrap;" +
      "overflow:hidden;text-overflow:ellipsis;" + EXPORT_FONT_IMPORTANT;

    const track = document.createElement("div");
    track.style.cssText =
      "flex:1;height:30px;background:#f1f5f9;border-radius:6px;";
    const bar = document.createElement("div");
    const pct = Math.max(2, (Math.abs(Number(e.value) || 0) / maxAbs) * 100);
    bar.style.cssText =
      `height:100%;width:${pct}%;border-radius:6px;background:${e.color || "#3b5bdb"};`;
    track.appendChild(bar);

    const val = document.createElement("div");
    val.textContent = e.display;
    val.style.cssText =
      "width:150px;text-align:right;font-size:19px;font-weight:600;color:#1e293b;" +
      EXPORT_FONT_IMPORTANT;

    row.appendChild(label);
    row.appendChild(track);
    row.appendChild(val);
    host.appendChild(row);
  });

  document.body.appendChild(host);
  try {
    const canvas = await html2canvas(host, {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
    });
    const out = resizeCanvasToWidth(canvas, targetWidthPx);
    return await new Promise((resolve) => out.toBlob(resolve, "image/jpeg"));
  } finally {
    document.body.removeChild(host);
  }
};

/**
 * Build a portrait-A4 PDF from pre-captured chart images (see
 * `captureReportItems`), one titled block per selection, flowing with
 * pagination. A cover header lists the mode, research topics, and timestamp.
 */
export const exportReportPdf = ({
  captures = [],
  selectedKeywords = [],
  selectedEntities = [],
  modeLabel = "",
  entityNoun = "Entities",
  timeRangeLabel = "",
  filename,
} = {}) => {
  if (!captures.length) return;

  const pdf = new jsPDF("portrait", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const M = PDF_MARGIN_MM;
  const contentW = pageW - M * 2;
  let y = M;

  // ── Cover header ──
  pdf.setFont("times", "bold");
  pdf.setFontSize(17);
  pdf.setTextColor(20, 30, 55);
  pdf.text("Benchmark report", M, y + 5);
  y += 12;

  const metaLine = (label, value) => {
    if (!value) return;
    pdf.setFont("times", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(71, 85, 105);
    const labelText = `${label}: `;
    pdf.text(labelText, M, y);
    const indent = pdf.getTextWidth(labelText);
    pdf.setFont("times", "normal");
    pdf.setTextColor(110, 120, 135);
    const lines = pdf.splitTextToSize(String(value), contentW - indent);
    pdf.text(lines, M + indent, y);
    y += lines.length * 5;
  };

  if (modeLabel) metaLine("Benchmark", modeLabel);
  if (timeRangeLabel) metaLine("Time range", timeRangeLabel);
  if (selectedKeywords.length) {
    metaLine("Research topics", selectedKeywords.map(titleCase).join(", "));
  }
  if (selectedEntities.length) {
    metaLine(entityNoun, selectedEntities.join(", "));
  }
  metaLine("Exported", new Date().toLocaleString());
  y += 4;

  // ── One block per selected chart: image · legend · stats table ──
  // The captured image already carries each chart's own title, so no extra
  // heading is printed here (it would duplicate the on-chart title).
  captures.forEach((cap, idx) => {
    let imgW = contentW;
    let imgH = cap.width ? (cap.height / cap.width) * imgW : 0;

    // Start the block on a fresh page if barely any room remains.
    if (y + 40 > pageH - M) {
      pdf.addPage();
      y = M;
    }

    // Every block gets one consistently-styled title drawn here. On-chart
    // titles are stripped from the snapshots (data-report-strip), so there is
    // no duplication and spacing is uniform across all charts and tables.
    if (cap.title) {
      pdf.setFont("times", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(20, 30, 55);
      pdf.text(`${idx + 1}. ${cap.title}`, M, y + 3.5);
      y += 8;
    }

    if (imgH) {
      // Fit within remaining page height; a chart taller than a full page
      // starts fresh and scales down to fit.
      const remaining = pageH - M - y;
      if (imgH > remaining) {
        if (remaining < (pageH - 2 * M) * 0.5) {
          pdf.addPage();
          y = M;
        }
        const avail = pageH - M - y;
        if (imgH > avail) {
          const ratio = avail / imgH;
          imgH = avail;
          imgW *= ratio;
        }
      }
      pdf.addImage(cap.dataUrl, "PNG", M, y, imgW, imgH);
      y += imgH + 6;
    }

    if (cap.legend?.length) {
      if (y + 8 > pageH - M) {
        pdf.addPage();
        y = M;
      }
      y = drawReportLegend(pdf, cap.legend, y, contentW) + 2;
    }

    if (cap.table?.rows?.length) {
      if (y + 16 > pageH - M) {
        pdf.addPage();
        y = M;
      }
      y = drawReportTable(pdf, cap.table, y, contentW, pageH);
    }

    y += 7;
  });

  pdf.save(filename || `benchmark_report_${fileTimestamp()}.pdf`);
};
